// @ts-check
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import './BindDeviceModal.css';

/**
 * Lightweight UDI/device-id format check. Permissive — server is the source
 * of truth — but blocks the obvious empty/whitespace/HTML mistakes that
 * window.prompt allowed through.
 *
 * @param {string} value
 */
function looksLikeDeviceId(value) {
  const trimmed = value.trim();
  if (trimmed.length < 3) return false;
  if (trimmed.length > 128) return false;
  if (/[\s<>"'`]/.test(trimmed)) return false;
  return true;
}

/**
 * Refresh the access token if it expires within `safetyWindowSec` seconds.
 * Resolves with whatever token is current after refresh (or original).
 *
 * @param {{ user?: { access_token?: string, expires_at?: number }, signinSilent: () => Promise<unknown> }} auth
 * @param {number} [safetyWindowSec]
 */
async function ensureFreshToken(auth, safetyWindowSec = 60) {
  const expiresAt = auth.user?.expires_at; // unix seconds
  const nowSec = Math.floor(Date.now() / 1000);
  if (!expiresAt || expiresAt - nowSec < safetyWindowSec) {
    try {
      await auth.signinSilent();
    } catch {
      // If silent renew fails, fall through with the existing (possibly
      // expired) token; the API will 401 and the caller surfaces that.
    }
  }
  return auth.user?.access_token;
}

/**
 * BindDeviceModal — replaces the legacy window.prompt() at LicenseTable.js.
 *
 * Submits via `onBind(serialNumber, deviceId, accessToken)`; the parent
 * handles the API call. Modal closes on success, surfaces server error
 * inline on failure (and keeps form state).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.serialNumber
 * @param {string} [props.currentDeviceId] - if set, this is a rebind
 * @param {{ user?: { access_token?: string, expires_at?: number }, signinSilent: () => Promise<unknown> }} props.auth
 * @param {(serialNumber: string, deviceId: string, accessToken: string) => Promise<void>} props.onBind
 */
export function BindDeviceModal({ isOpen, onClose, serialNumber, currentDeviceId, auth, onBind }) {
  const [deviceId, setDeviceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));

  // Reset form whenever the modal opens for a new license.
  useEffect(() => {
    if (isOpen) {
      setDeviceId('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen, serialNumber]);

  // Auto-focus the input on open.
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Defer to allow focus-trap to activate first.
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const valid = looksLikeDeviceId(deviceId);
  const isRebind = Boolean(currentDeviceId);
  const titleId = 'bind-device-modal-title';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const accessToken = await ensureFreshToken(auth);
      if (!accessToken) {
        setError('Session expired. Please sign in again to continue.');
        setSubmitting(false);
        return;
      }
      await onBind(serialNumber, deviceId.trim(), accessToken);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Bind failed. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} labelledBy={titleId} dismissable={!submitting}>
      <Modal.Header id={titleId}>
        {isRebind ? 'Rebind device' : 'Bind device'}
        {' '}
        for <span className="bind-modal__serial">{serialNumber}</span>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="bind-modal__form" id="bind-device-form">
          {isRebind && (
            <p className="bind-modal__hint" role="status">
              Currently bound to <code className="bind-modal__current">{currentDeviceId}</code>.
              Submitting will replace the binding.
            </p>
          )}
          <label htmlFor="bind-device-input" className="form-label">
            Device ID
          </label>
          <input
            id="bind-device-input"
            ref={inputRef}
            type="text"
            className="form-input bind-modal__input"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="e.g. dev-abc123def456"
            autoComplete="off"
            spellCheck={false}
            disabled={submitting}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'bind-device-error' : 'bind-device-help'}
          />
          <p id="bind-device-help" className="form-hint">
            3–128 characters. No spaces, quotes, or angle brackets.
          </p>
          {error && (
            <p id="bind-device-error" className="form-message form-message--error" role="alert">
              {error}
            </p>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={!valid || submitting}
          loading={submitting}
          onClick={handleSubmit}
        >
          {isRebind ? 'Rebind' : 'Bind'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
